import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonTable from "../../../components/Table/Table";
import "./ProductList.css";
import {
  getAllProduct,
  deleteProduct,
  updateProductStatusApi,
  toggleProductActiveApi,
} from "../../../services/Products/Product.service";
import { ProductTypes } from "../../../services/Products/Product.types";
import {
  Pencil,
  Eye,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import { getCategories } from "../../../services/Category/category.service";
import { getAllDesired } from "../../../services/DesiredType/DesiredType.service";
import { environment } from "../../../environments/environment";
import { getUOM } from "../../../services/UOM/uom.service";

interface ExtendedProduct extends ProductTypes {
  article_master?: { articleName: string };
  category?: { name: string };
  uom?: { name: string; id?: number };
  UomId?: number | null;
}

const ProductList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<ExtendedProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ExtendedProduct | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("Pending");
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState<any[]>([]);
  const [metals, setMetals] = useState<any[]>([]);

  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMetal, setSelectedMetal] = useState("");
  const [selectedArticle, setSelectedArticle] = useState("");
  const [selectedSeller, setSelectedSeller] = useState("");

  const [uoms, setUoms] = useState<any[]>([]);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const calledOnce = useRef(false);

  // ✅ Helper function to safely get images as array
  const getImageArray = (images: any): string[] => {
    if (!images) return [];
    if (Array.isArray(images)) return images;
    if (typeof images === 'string') return [images];
    return [];
  };

  // ✅ Helper function to get full image URL
  const getFullImageUrl = (imagePath: string): string => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${environment.fileUrl}/${cleanPath}`;
  };

  const getArticleName = (product: ExtendedProduct): string => {
    if (product.articleName) return product.articleName;
    if (product.article_master?.articleName) return product.article_master.articleName;
    return "-";
  };

  const getCategoryName = (product: ExtendedProduct): string => {
    if (product.category_name) return product.category_name;
    if (product.category?.name) return product.category.name;
    if (product.categoryId) {
      const found = categories.find(c => String(c.id) === String(product.categoryId));
      if (found) return found.name;
    }
    return "-";
  };

const getUomName = (product: ExtendedProduct): string => {
  // Agar uom object hai aur usme name hai
  if (product.uom && typeof product.uom === 'object' && product.uom.name) {
    return product.uom.name;
  }
  
  // Agar uom string hai
  if (typeof product.uom === 'string') {
    return product.uom;
  }
  
  // ✅ UOM list se match karein
  if (product.UomId) {
    const found = uoms.find((u: any) => String(u.id) === String(product.UomId));
    if (found) return found.name;
  }
  
  // Agar UomId nahi hai toh default
  return "-";
};




// ✅ UOM fetch function
const loadUOMs = async () => {
  try {
    const data = await getUOM();
    setUoms(data);
  } catch (error) {
    console.error("Failed to fetch UOMs:", error);
  }
};
  const loadProducts = async () => {
    try {
      const data: any = await getAllProduct();
      setProducts(data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      setProducts([]);
      toast.error("Failed to load products");
    }
  };

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const getAvailableQuantity = (product: ExtendedProduct): number => {
    if (product.inventory && product.inventory.availableQuantity !== undefined) {
      return product.inventory.availableQuantity;
    }
    return product.quantity ?? 0;
  };

  const loadMetals = async () => {
    try {
      const res = await getAllDesired();
      setMetals(res.data);
    } catch (error) {
      console.error("Failed to fetch metals", error);
    }
  };

  const toggleActive = async (id: number) => {
    try {
      await toggleProductActiveApi(id);
      setProducts(prev =>
        prev.map(p => (p.id === id ? { ...p, isActive: !p.isActive } : p))
      );
      toast.success("Product status updated");
    } catch (error) {
      toast.error("Failed to update active status");
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedProduct?.id) return;
    try {
      await updateProductStatusApi(selectedProduct.id, { status: selectedStatus });
      toast.success("Status updated successfully");
      await loadProducts();
      setSelectedProduct(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (id: number | undefined) => {
    if (!id) {
      toast.error("Invalid product ID");
      return;
    }
    try {
      const response = await deleteProduct(id);
      if (response.success) {
        await loadProducts();
        toast.success(response.message || "Deleted successfully");
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Failed to delete product");
    }
  };

  // ✅ FIXED: Open gallery with safe image array
  const openGallery = (images: any) => {
    const imageArray = getImageArray(images);
    if (!imageArray || imageArray.length === 0) {
      toast.info("No images available");
      return;
    }
    setGalleryImages(imageArray);
    setCurrentImageIndex(0);
    setGalleryModalOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    if (calledOnce.current) return;
    calledOnce.current = true;
    loadProducts();
    loadCategories();
    loadMetals();
     loadUOMs();
  }, []);

  const uniqueArticles = React.useMemo(() => {
    const articles = products.map(p => getArticleName(p)).filter(name => name !== "-");
    return Array.from(new Set(articles)).sort();
  }, [products]);

  const uniqueSellers = React.useMemo(() => {
    const sellers = products
      .map(p => {
        const seller = p.buyers_sellers_detail;
        if (!seller) return null;
        const fullName = `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
        return fullName || seller.businessName || null;
      })
      .filter((name): name is string => !!name && name !== "");
    return Array.from(new Set(sellers)).sort();
  }, [products]);

  const filtered = products.filter((p) => {
    const searchTerm = search.trim().toLowerCase();
    const articleDisplay = getArticleName(p);
    const sellerName = (() => {
      const seller = p.buyers_sellers_detail;
      if (!seller) return "";
      const fullName = `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
      return fullName || seller.businessName || "";
    })();

    const matchSearch = searchTerm === "" || articleDisplay.toLowerCase().includes(searchTerm);
    const matchCategory = selectedCategory === "" || String(p.categoryId) === String(selectedCategory);
    const matchMetal = selectedMetal === "" || String(p.MetalTypeId) === String(selectedMetal);
    const matchArticle = selectedArticle === "" || articleDisplay === selectedArticle;
    const matchSeller = selectedSeller === "" || sellerName === selectedSeller;

    return matchSearch && matchCategory && matchMetal && matchArticle && matchSeller;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedData = filtered
    .slice(startIndex, startIndex + itemsPerPage)
    .map((item, index) => ({
      ...item,
      serial: startIndex + index + 1,
    }));

  const columns = [
    { key: "serial", label: "S.NO" },
    {
      key: "image",
      label: "Image",
      render: (value: any, row: ExtendedProduct) => {
        const images = getImageArray(value);
        if (images.length === 0) {
          return <span className="no-image-placeholder">No Image</span>;
        }
        const imageUrl = getFullImageUrl(images[0]);
        return (
          <img
            src={imageUrl}
            alt="product"
            className="product-img"
            onClick={() => openGallery(images)}
            style={{ cursor: "pointer", width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                const fallback = document.createElement('span');
                fallback.textContent = 'No Image';
                fallback.style.color = '#9ca3af';
                fallback.style.fontSize = '12px';
                parent.appendChild(fallback);
              }
            }}
          />
        );
      },
    },
    {
      key: "articleName",
      label: "Article Name",
      render: (_: any, row: ExtendedProduct) => getArticleName(row),
    },
    {
      key: "category_name",
      label: "Category",
      render: (_: any, row: ExtendedProduct) => getCategoryName(row),
    },
    {
      key: "MetalTypeId",
      label: "Metal Type",
      render: (value: number) => {
        const metal = metals.find((m: any) => String(m.id) === String(value));
        return metal?.name || value || "-";
      },
    },
    {
      key: "uom",
      label: "UOM",
      render: (_: any, row: ExtendedProduct) => getUomName(row),
    },
    {
      key: "seller_name",
      label: "Seller Name",
      render: (_: any, row: ExtendedProduct) => {
        const seller = row.buyers_sellers_detail;
        if (!seller) return "-";
        const fullName = `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
        return fullName || seller.businessName || "-";
      },
    },
    {
      key: "seller_type",
      label: "Seller Type",
      render: (_: any, row: ExtendedProduct) => {
        const seller = row.buyers_sellers_detail;
        return seller?.Type_of_business?.name || "N/A";
      },
    },
    {
      key: "businessName",
      label: "Business Name",
      render: (_: any, row: ExtendedProduct) => {
        const seller = row.buyers_sellers_detail;
        return seller?.businessName?.trim() || "N/A";
      },
    },
    {
      key: "seller_mobile",
      label: "Seller Mobile",
      render: (_: any, row: ExtendedProduct) => {
        const seller = row.buyers_sellers_detail;
        return seller?.mobileNumber || "N/A";
      },
    },
    {
      key: "primary_deal_in",
      label: "Primary Deal In",
      render: (_: any, row: ExtendedProduct) => {
        const seller = row.buyers_sellers_detail;
        return seller?.product_information_master?.name || "N/A";
      },
    },
    {
      key: "purity",
      label: "Purity",
      render: (value: string, row: any) => {
        if (row.karat) return `${row.karat} (${value}%)`;
        if (row.carat) return `${row.carat} Carat`;
        return value || "-";
      },
    },
    {
      key: "availableQuantity",
      label: "Available Quantity",
      render: (_: any, row: ExtendedProduct) => {
        const availableQty = getAvailableQuantity(row);
        const uom = getUomName(row);
        return uom !== "-" ? `${availableQty} ${uom}` : `${availableQty}`;
      },
    },
    {
      key: "weight",
      label: "Weight per Article",
      render: (value: string) => value || "-",
    },
    {
      key: "makingCharges",
      label: "Making Charges",
      render: (value: number) => (value ? `${value}` : "-"),
    },
    {
      key: "isActive",
      label: "Product Status",
      render: (_: any, row: ExtendedProduct) => (
        <button
          className={`status-pill ${row.isActive ? "active" : "inactive"}`}
          onClick={() => row.id && toggleActive(row.id)}
        >
          {row.isActive ? "Active" : "Inactive"}
        </button>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (value: string) => (
        <span className={`status-badge ${value?.toLowerCase() || "pending"}`}>
          {value || "pending"}
        </span>
      ),
    },
  ];

  return (
    <div className="category-container">
      <div className="header-bar">
        <div className="filter-wrapper">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-input"
          />

          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Category</option>
            {categories.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={selectedMetal}
            onChange={(e) => {
              setSelectedMetal(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Metal</option>
            {metals.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>

          <select
            value={selectedArticle}
            onChange={(e) => {
              setSelectedArticle(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Articles</option>
            {uniqueArticles.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={selectedSeller}
            onChange={(e) => {
              setSelectedSeller(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Sellers</option>
            {uniqueSellers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <button className="add-btn" onClick={() => navigate("/add/product")}>
          Add Product
        </button>
      </div>

      <CommonTable
        columns={columns}
        data={paginatedData}
        tableClassName="compact-table"
        actions={(row) => (
          <div className="action-btn">
            <button
              className="icon-btn view"
              onClick={() => {
                setSelectedProduct(row);
                setSelectedStatus(row.status || "Pending");
              }}
              title="View"
            >
              <Eye size={18} />
            </button>
            <button
              className="icon-btn edit"
              onClick={() => navigate(`/edit/product/${row.id}`)}
              title="Edit"
            >
              <Pencil size={18} />
            </button>
            <button
              className="icon-btn delete"
              onClick={() => row.id && handleDelete(row.id)}
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
      />

      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>
            Previous
          </button>
          <span>Page {currentPage} of {totalPages} (Total {filtered.length} items)</span>
          <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      )}

      {/* Product Details Modal */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Product Details</h3>
            <table className="details-table">
              <tbody>
                <tr><th>ID</th><td>{selectedProduct.id}</td></tr>
                <tr><th>Article Name</th><td>{getArticleName(selectedProduct)}</td></tr>
                <tr><th>Category</th><td>{getCategoryName(selectedProduct)}</td></tr>
                <tr>
                  <th>Metal Type</th>
                  <td>
                    {metals.find((m: any) => String(m.id) === String(selectedProduct.MetalTypeId))?.name ||
                      selectedProduct.MetalTypeName ||
                      selectedProduct.MetalTypeId ||
                      "-"}
                  </td>
                </tr>
                <tr>
                  <th>UOM</th>
                  <td>{getUomName(selectedProduct)}</td>
                </tr>
                <tr>
                  <th>Seller Name</th>
                  <td>{(() => {
                    const seller = selectedProduct.buyers_sellers_detail;
                    if (!seller) return "-";
                    const fullName = `${seller.firstName || ""} ${seller.lastName || ""}`.trim();
                    return fullName || seller.businessName || "-";
                  })()}</td>
                </tr>
                <tr>
                  <th>Business Name</th>
                  <td>{selectedProduct.buyers_sellers_detail?.businessName || "-"}</td>
                </tr>
                <tr>
                  <th>Primary Deal In</th>
                  <td>{selectedProduct.buyers_sellers_detail?.product_information_master?.name || "-"}</td>
                </tr>
                <tr>
                  <th>Seller Type</th>
                  <td>{selectedProduct.buyers_sellers_detail?.Type_of_business?.name || "-"}</td>
                </tr>
                <tr>
                  <th>Seller Mobile</th>
                  <td>{selectedProduct.buyers_sellers_detail?.mobileNumber || "-"}</td>
                </tr>
                <tr>
                  <th>Purity/Karat</th>
                  <td>
                    {selectedProduct.karat && `${selectedProduct.karat} `}
                    {selectedProduct.purity && `(${selectedProduct.purity}%)`}
                    {selectedProduct.carat && `${selectedProduct.carat} Carat`}
                    {selectedProduct.centValue && ` - ${selectedProduct.centValue} Cent`}
                    {!selectedProduct.purity && !selectedProduct.carat && "-"}
                  </td>
                </tr>
                <tr>
                  <th>Description</th>
                  <td>{selectedProduct.description || "-"}</td>
                </tr>
                <tr>
                  <th>Available Quantity</th>
                  <td>
                    {getAvailableQuantity(selectedProduct)} {getUomName(selectedProduct) !== "-" ? getUomName(selectedProduct) : ""}
                  </td>
                </tr>
                <tr>
                  <th>Weight per Article</th>
                  <td>{selectedProduct.weight || "-"}</td>
                </tr>
                <tr>
                  <th>Dimensions</th>
                  <td>
                    {selectedProduct.Dimensions?.[0]
                      ? `${selectedProduct.Dimensions[0].length}, ${selectedProduct.Dimensions[0].width}, ${selectedProduct.Dimensions[0].height}`
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <th>Wastage</th>
                  <td>{selectedProduct.wastage ?? 0}</td>
                </tr>
                <tr>
                  <th>Making Charges</th>
                  <td>₹{selectedProduct.makingCharges ?? 0}</td>
                </tr>
                <tr>
                  <th>T2T</th>
                  <td>{selectedProduct.t2t ? "Yes" : "No"}</td>
                </tr>
                {selectedProduct.difference && (
                  <tr><th>Difference</th><td>{selectedProduct.difference}</td></tr>
                )}
                <tr>
                  <th>Availability</th>
                  <td>{selectedProduct.availabilityStatus || "-"}</td>
                </tr>
                <tr>
                  <th>Images</th>
                  <td>
                    {(() => {
                      const images = getImageArray(selectedProduct.image);
                      if (images.length === 0) return "-";
                      return (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          {images.map((img: string, idx: number) => (
                            <img
                              key={idx}
                              src={getFullImageUrl(img)}
                              alt={`product-${idx}`}
                              style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "6px", cursor: "pointer", border: "1px solid #ddd" }}
                              onClick={() => openGallery(images)}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <select
                      className="status-dropdown"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <th>Created At</th>
                  <td>{selectedProduct.createdAt ? new Date(selectedProduct.createdAt).toLocaleString() : "-"}</td>
                </tr>
              </tbody>
            </table>
            <button className="update-btn" onClick={handleStatusUpdate}>Update Status</button>
            <button className="close-btnn" onClick={() => setSelectedProduct(null)}>Close</button>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryModalOpen && (
        <div className="modal-overlay gallery-modal" onClick={() => setGalleryModalOpen(false)}>
          <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-close" onClick={() => setGalleryModalOpen(false)}>
              <X size={24} />
            </button>
            <div className="gallery-container">
              <button
                className="gallery-nav prev"
                onClick={prevImage}
                disabled={galleryImages.length <= 1}
              >
                <ChevronLeft size={36} />
              </button>
              <div className="gallery-image-wrapper">
                <img
                  src={getFullImageUrl(galleryImages[currentImageIndex] || "")}
                  alt={`Product ${currentImageIndex + 1}`}
                  className="gallery-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50" y="100" font-family="Arial" font-size="14" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
                <div className="image-counter">
                  {currentImageIndex + 1} / {galleryImages.length}
                </div>
              </div>
              <button
                className="gallery-nav next"
                onClick={nextImage}
                disabled={galleryImages.length <= 1}
              >
                <ChevronRight size={36} />
              </button>
            </div>
            {galleryImages.length > 1 && (
              <div className="thumbnail-container">
                {galleryImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={getFullImageUrl(img)}
                    alt={`Thumb ${idx + 1}`}
                    className={`thumbnail ${idx === currentImageIndex ? "active" : ""}`}
                    onClick={() => setCurrentImageIndex(idx)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;